import { DEFAULT_ROUND_CONFIG, type Attempt, type KnockoutMatchState, type MatchState } from '@ranked-waiting/game-core';
import { renderRoundStage } from './round-stage';
import { escapeHtml, formatErrorMs } from './format';
import { resultBadgeMarkup } from './result-badge';
import { accuracyBarMarkup } from './accuracy-bar';
import { fetchCatalog, fetchInventory, resolvePlayerTag, type CatalogItem } from './catalog';
import { fetchMe } from './session';
import { checkForNewAchievements } from './achievement-toast';

type FinalDuelSlot = 'a' | 'b';

interface JoinedMessage {
  type: 'joined';
  userId: string;
  roster: string[];
}
interface ResumedMessage {
  type: 'resumed';
  userId: string;
  match: KnockoutMatchState;
  currentTargetMs: number | null;
  prepareDelayMs: number | null;
  alreadyAttempted: boolean;
}
interface LobbyPresenceMessage {
  type: 'lobby-presence';
  connected: Record<string, boolean>;
  usernames: Record<string, string>;
  remaining: string[];
  roster: string[];
}
interface PlayerDisconnectedMessage {
  type: 'player-disconnected';
  userId: string;
  graceMs: number;
}
interface PlayersForfeitedMessage {
  type: 'players-forfeited';
  ids: string[];
  match: KnockoutMatchState;
}
interface FinalDuelStartMessage {
  type: 'final-duel-start';
  slotUserIds: Record<FinalDuelSlot, string>;
  match: KnockoutMatchState;
}
interface RoundTargetMessage {
  type: 'round-target';
  phase: 'elimination' | 'final_duel';
  roundNumber: number;
  targetMs: number;
  prepareDelayMs: number;
  remaining: string[];
  slotUserIds?: Record<FinalDuelSlot, string>;
}
interface RoundResultMessage {
  type: 'round-result';
  phase: 'elimination' | 'final_duel';
  match: KnockoutMatchState;
  finalDuel?: MatchState;
  attempts: Record<string, Attempt>;
  ratingDeltas: Record<string, number> | null;
}
interface MatchCompleteMessage {
  type: 'match-complete';
  match: KnockoutMatchState;
  finalDuel?: MatchState | null;
  ratingDeltas: Record<string, number> | null;
}
interface ErrorMessage {
  type: 'error';
  message: string;
}
type ServerMessage =
  | JoinedMessage
  | ResumedMessage
  | LobbyPresenceMessage
  | PlayerDisconnectedMessage
  | PlayersForfeitedMessage
  | FinalDuelStartMessage
  | RoundTargetMessage
  | RoundResultMessage
  | MatchCompleteMessage
  | ErrorMessage;

export interface KnockoutSessionOptions {
  onExit: () => void;
}

/** Milliseconds between each row's staggered reveal in a round's result list — the doc's "results cascade into position" beat. */
const REVEAL_STEP_MS = 180;
/** Delay after a row reveals before its elimination (fade/scale-out) visually lands, so removal reads as a distinct second beat. */
const ELIMINATE_FOLLOW_MS = 220;

/**
 * Wires a connected `KnockoutLobby` WebSocket to the lobby/round/elimination
 * UI, including the final best-of-5 duel once exactly 2 players remain.
 * Same shape as `duel-session.ts`'s `attachDuelSession` — a `uiState`
 * machine gates what a broadcast is allowed to (re)render, since a stray
 * `lobby-presence` update must not wipe an active round the way a stray
 * `presence` broadcast must not in the 1v1 duel session.
 */
export function attachKnockoutSession(mount: HTMLElement, socket: WebSocket, options: KnockoutSessionOptions): () => void {
  let myUserId: string | null = null;
  let destroyRound: (() => void) | void;
  let destroyed = false;
  let roster: readonly string[] = [];
  let usernames: Record<string, string> = {};
  let connected: Record<string, boolean> = {};
  let remaining: readonly string[] = [];
  let myTag: { emblemImage: string | null; plateImage: string | null } | null = null;
  let finalDuelSlotUserIds: Record<FinalDuelSlot, string> | null = null;

  let uiState: 'lobby' | 'round' | 'between' | 'eliminated-spectating' | 'complete' | 'error' = 'lobby';

  mount.innerHTML = `<div class="session-content" id="knockout-content"></div>`;
  const contentEl = mount.querySelector<HTMLDivElement>('#knockout-content')!;

  // Best-effort: my own equipped emblem/plate for the lobby grid. Other
  // players show name + status only — there is no public per-user cosmetic
  // lookup today (see the knockout mode plan's client section).
  void (async () => {
    try {
      const [me, catalog, inventory] = await Promise.all([fetchMe(), fetchCatalog(), fetchInventory()]);
      if (!me || destroyed) return;
      const byId = new Map<string, CatalogItem>(catalog.items.map((i) => [i.id, i]));
      const { emblemImage, plateImage } = resolvePlayerTag(byId, inventory.equipped);
      myTag = { emblemImage, plateImage };
      if (uiState === 'lobby') renderLobby();
    } catch {
      /* the grid still works without art */
    }
  })();

  function tileMarkup(userId: string): string {
    const isMe = userId === myUserId;
    const name = usernames[userId] ?? (isMe ? 'You' : 'Player');
    const isEliminated = !remaining.includes(userId);
    const isConnected = connected[userId] ?? false;
    const emblemStyle = isMe && myTag?.emblemImage ? `background-image:url('${myTag.emblemImage}');` : '';
    return `
      <div class="knockout-tile${isEliminated ? ' knockout-tile--eliminated' : ''}" data-user-id="${escapeHtml(userId)}">
        <div class="knockout-tile__emblem" style="${emblemStyle}"></div>
        <strong class="knockout-tile__name">${escapeHtml(name)}${isMe ? ' (you)' : ''}</strong>
        <span class="knockout-tile__status">${isEliminated ? 'Eliminated' : isConnected ? 'Ready' : 'Connecting…'}</span>
      </div>
    `;
  }

  function renderLobby() {
    const connectedCount = roster.filter((id) => connected[id]).length;
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Knockout lobby: ${connectedCount} of ${roster.length} connected</p>
        <div class="knockout-grid">${roster.map(tileMarkup).join('')}</div>
        <p class="round-stage__hint">Round 1 starts automatically once everyone has joined.</p>
      </div>
    `;
  }

  function renderEliminatedSpectating(roundNumber: number) {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">You were eliminated, round ${roundNumber}</p>
        <div class="knockout-grid">${roster.map(tileMarkup).join('')}</div>
        <p class="round-stage__hint">${remaining.length} player${remaining.length === 1 ? '' : 's'} still competing.</p>
      </div>
    `;
  }

  /**
   * The elimination round reveal: a "CUT" line (the worst surviving error —
   * everyone below it qualified, everyone at/above it didn't), then every
   * participant's own result revealed worst-to-best in a short staggered
   * sequence, eliminated rows cascading out a beat after they reveal. All of
   * this reads data the server already sent in `msg.attempts` — no new
   * fetches, purely a presentation pass (owner decision, 2026-08-11, per the
   * battle-royale design doc's cut-line/cascade beats).
   */
  function renderEliminationAnnouncement(msg: RoundResultMessage) {
    const lastEntry = msg.match.eliminatedByRound[msg.match.eliminatedByRound.length - 1];
    const isTimingCut = (lastEntry?.cutPercent ?? 0) > 0; // false for a forfeit (0) or the final-duel handoff (-1) — neither is a "cut line" moment
    const eliminatedSet = new Set(lastEntry?.eliminated ?? []);
    const participantIds = Object.keys(msg.attempts);
    const sortedWorstFirst = [...participantIds].sort(
      (a, b) => msg.attempts[b].errorMs - msg.attempts[a].errorMs,
    );

    let cutLineHtml = '';
    if (isTimingCut) {
      const survivorErrors = msg.match.remaining
        .map((id) => msg.attempts[id]?.errorMs)
        .filter((e): e is number => e !== undefined && Number.isFinite(e));
      if (survivorErrors.length > 0) {
        cutLineHtml = `<p class="knockout-cutline">CUT: ${formatErrorMs(Math.max(...survivorErrors))}</p>`;
      }
    }

    const amEliminated = myUserId !== null && eliminatedSet.has(myUserId);
    const iCanContinue = myUserId !== null && msg.match.remaining.includes(myUserId);

    contentEl.innerHTML = `
      <div class="round-result-pair">
        <p class="handoff__label">Round ${lastEntry?.round ?? msg.match.roundNumber} results</p>
        ${cutLineHtml}
        <ul class="attempt-list knockout-reveal-list">
          ${sortedWorstFirst
            .map((id) => {
              const attempt = msg.attempts[id];
              const isElim = eliminatedSet.has(id);
              const verdict = isTimingCut ? (isElim ? 'eliminated' : 'qualified') : isElim ? 'eliminated' : '';
              return `<li class="knockout-reveal-row" data-eliminated="${isElim}">
                <strong>${escapeHtml(usernames[id] ?? 'Player')}</strong>
                ${resultBadgeMarkup(attempt)}
                ${verdict ? `<em>${verdict}</em>` : ''}
              </li>`;
            })
            .join('')}
        </ul>
        <p class="handoff__label knockout-reveal-followup" id="knockout-remaining-count">${msg.match.remaining.length} player${msg.match.remaining.length === 1 ? '' : 's'} remain</p>
        <div class="knockout-reveal-followup" id="knockout-continue-slot"></div>
      </div>
    `;

    const rows = contentEl.querySelectorAll<HTMLLIElement>('.knockout-reveal-row');
    rows.forEach((row, index) => {
      window.setTimeout(() => {
        row.classList.add('knockout-reveal-row--visible');
        if (row.dataset.eliminated === 'true') {
          window.setTimeout(() => row.classList.add('knockout-reveal-row--eliminated'), ELIMINATE_FOLLOW_MS);
        }
      }, index * REVEAL_STEP_MS);
    });

    window.setTimeout(
      () => {
        contentEl.querySelectorAll<HTMLElement>('.knockout-reveal-followup').forEach((el) => el.classList.add('knockout-reveal-followup--visible'));
        const slot = contentEl.querySelector<HTMLDivElement>('#knockout-continue-slot');
        if (!slot) return;
        if (amEliminated) {
          slot.innerHTML = `<p class="round-stage__hint">You're spectating the rest of this match.</p>`;
        } else if (iCanContinue) {
          slot.innerHTML = `<button class="button button--primary" type="button" data-action="continue">Continue</button>`;
          slot.querySelector<HTMLButtonElement>('[data-action="continue"]')?.addEventListener('click', (event) => {
            const button = event.currentTarget as HTMLButtonElement;
            button.disabled = true;
            button.textContent = 'Waiting for the field…';
            socket.send(JSON.stringify({ type: 'continue' }));
          });
        }
      },
      rows.length * REVEAL_STEP_MS + ELIMINATE_FOLLOW_MS,
    );
  }

  /** FINAL DUEL — BEST OF 5. Duelists get the normal press UI; everyone else spectates a read-only view of the same broadcasts. */
  function renderFinalDuelStage(msg: RoundTargetMessage) {
    const slotUserIds = msg.slotUserIds ?? finalDuelSlotUserIds;
    if (!slotUserIds) return;
    finalDuelSlotUserIds = slotUserIds;
    const amDuelist = myUserId !== null && (slotUserIds.a === myUserId || slotUserIds.b === myUserId);

    if (!amDuelist) {
      contentEl.innerHTML = `
        <div class="handoff knockout-final-duel">
          <p class="handoff__label">FINAL DUEL: BEST OF 5</p>
          <h2>${escapeHtml(usernames[slotUserIds.a] ?? 'Player')} vs ${escapeHtml(usernames[slotUserIds.b] ?? 'Player')}</h2>
          <p class="round-stage__hint">Round ${msg.roundNumber} in progress, spectating.</p>
        </div>
      `;
      return;
    }

    destroyRound = renderRoundStage(contentEl, {
      config: DEFAULT_ROUND_CONFIG,
      fixedTargetMs: msg.targetMs,
      fixedPrepareDelayMs: msg.prepareDelayMs,
      label: `FINAL DUEL: round ${msg.roundNumber}`,
      onArm: () => socket.send(JSON.stringify({ type: 'arm', roundNumber: msg.roundNumber })),
      onStop: (attempt) => {
        socket.send(
          JSON.stringify({
            type: 'stop',
            roundNumber: msg.roundNumber,
            elapsedMs: attempt.elapsedMs,
            isFalseStart: attempt.isFalseStart,
          }),
        );
        if (destroyRound) destroyRound();
        destroyRound = undefined;
        renderWaitingForField();
      },
    });
  }

  /** A completed (but not match-deciding) duel round: the running best-of-5 scoreline, both attempts, and a continue gate for the 2 duelists only. */
  function renderFinalDuelRoundResult(msg: RoundResultMessage) {
    const slotUserIds = finalDuelSlotUserIds;
    const duel = msg.finalDuel;
    if (!slotUserIds || !duel) return;
    const attemptA = msg.attempts[slotUserIds.a];
    const attemptB = msg.attempts[slotUserIds.b];
    const amDuelist = myUserId !== null && (slotUserIds.a === myUserId || slotUserIds.b === myUserId);

    contentEl.innerHTML = `
      <div class="round-result-pair knockout-final-duel">
        <p class="handoff__label">FINAL DUEL: ${duel.scores.a}-${duel.scores.b}</p>
        <div class="round-result-pair__grid">
          <div class="round-result${attemptA?.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>${escapeHtml(usernames[slotUserIds.a] ?? 'Player')}${slotUserIds.a === myUserId ? ' (you)' : ''}</span>${attemptA ? resultBadgeMarkup(attemptA) : ''}</div>
            ${attemptA ? accuracyBarMarkup(attemptA) : ''}
          </div>
          <div class="round-result${attemptB?.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>${escapeHtml(usernames[slotUserIds.b] ?? 'Player')}${slotUserIds.b === myUserId ? ' (you)' : ''}</span>${attemptB ? resultBadgeMarkup(attemptB) : ''}</div>
            ${attemptB ? accuracyBarMarkup(attemptB) : ''}
          </div>
        </div>
        ${amDuelist ? `<button class="button button--primary" type="button" data-action="continue">Continue</button>` : `<p class="round-stage__hint">Spectating the final duel.</p>`}
      </div>
    `;

    if (amDuelist) {
      const button = contentEl.querySelector<HTMLButtonElement>('[data-action="continue"]');
      button?.addEventListener('click', () => {
        button.disabled = true;
        button.textContent = 'Waiting for the other duelist…';
        socket.send(JSON.stringify({ type: 'continue' }));
      });
    }
  }

  function renderComplete(match: KnockoutMatchState, ratingDeltas: Record<string, number> | null, finalDuel?: MatchState | null) {
    const isDraw = myUserId !== null && (match.finalTieGroup?.includes(myUserId) ?? false);
    const isWinner = myUserId !== null && match.winner === myUserId && !isDraw;
    const label = isDraw
      ? 'Match ended in a draw — the field tied at the very end.'
      : isWinner
        ? 'You win the knockout!'
        : `${match.winner ? escapeHtml(usernames[match.winner] ?? 'Another player') : 'Nobody'} wins the knockout.`;
    const myDelta = myUserId && ratingDeltas ? ratingDeltas[myUserId] : undefined;
    const ratingLine = myDelta !== undefined ? `<p class="handoff__label">Rating ${myDelta >= 0 ? '+' : ''}${myDelta}</p>` : '';
    const duelLine =
      finalDuel && finalDuelSlotUserIds
        ? `<p class="round-stage__hint">Final duel: ${escapeHtml(usernames[finalDuelSlotUserIds.a] ?? 'Player')} ${finalDuel.scores.a}-${finalDuel.scores.b} ${escapeHtml(usernames[finalDuelSlotUserIds.b] ?? 'Player')}</p>`
        : '';

    // Placement list, best (lowest number) first: winner (and any draw group) at 1,
    // then each eliminated group in reverse elimination order.
    const placementRows: { userId: string; placement: number }[] = [];
    if (match.finalTieGroup) {
      for (const id of match.finalTieGroup) placementRows.push({ userId: id, placement: 1 });
    } else if (match.winner) {
      placementRows.push({ userId: match.winner, placement: 1 });
    }
    let remainingCount = roster.length;
    for (const entry of match.eliminatedByRound) {
      for (const id of entry.eliminated) {
        if (!placementRows.some((r) => r.userId === id)) placementRows.push({ userId: id, placement: remainingCount });
      }
      remainingCount -= entry.eliminated.length;
    }
    placementRows.sort((a, b) => a.placement - b.placement);

    contentEl.innerHTML = `
      <div class="handoff">
        <h2>${label}</h2>
        ${ratingLine}
        ${duelLine}
        <ol class="attempt-list">
          ${placementRows
            .map(
              (row) =>
                `<li><strong>#${row.placement}</strong> ${escapeHtml(usernames[row.userId] ?? 'Player')}${row.userId === myUserId ? ' (you)' : ''}</li>`,
            )
            .join('')}
        </ol>
        <div class="hero-actions">
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => options.onExit());
  }

  function renderError(message: string) {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${escapeHtml(message)}</h2>
        <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => options.onExit());
  }

  function renderWaitingForField() {
    contentEl.innerHTML = `<div class="handoff"><p class="handoff__label">Waiting for the rest of the field…</p></div>`;
  }

  function handleServerMessage(msg: ServerMessage) {
    if (destroyed) return;

    if (msg.type === 'joined') {
      myUserId = msg.userId;
      roster = msg.roster;
      return;
    }
    if (msg.type === 'resumed') {
      myUserId = msg.userId;
      remaining = msg.match.remaining;
      return;
    }
    if (msg.type === 'lobby-presence') {
      roster = msg.roster;
      usernames = { ...usernames, ...msg.usernames };
      connected = msg.connected;
      remaining = msg.remaining;
      if (uiState === 'lobby') renderLobby();
      return;
    }
    if (msg.type === 'player-disconnected' || msg.type === 'players-forfeited') {
      // A grid re-render on the next lobby-presence/round broadcast picks
      // this up naturally; nothing to do here beyond letting it happen.
      return;
    }
    if (msg.type === 'final-duel-start') {
      finalDuelSlotUserIds = msg.slotUserIds;
      // The duel's first round-target follows immediately from the server;
      // nothing to render here beyond remembering who's dueling.
      return;
    }
    if (msg.type === 'round-target') {
      remaining = msg.remaining;
      if (msg.phase === 'final_duel') {
        uiState = 'round';
        if (destroyRound) destroyRound();
        renderFinalDuelStage(msg);
        return;
      }
      if (myUserId && !remaining.includes(myUserId)) {
        // I'm eliminated but the match continues — keep spectating rather
        // than trying to render a round stage I'm not part of.
        uiState = 'eliminated-spectating';
        if (destroyRound) destroyRound();
        destroyRound = undefined;
        renderEliminatedSpectating(msg.roundNumber);
        return;
      }
      uiState = 'round';
      if (destroyRound) destroyRound();
      destroyRound = renderRoundStage(contentEl, {
        config: DEFAULT_ROUND_CONFIG,
        fixedTargetMs: msg.targetMs,
        fixedPrepareDelayMs: msg.prepareDelayMs,
        label: `Round ${msg.roundNumber}: ${remaining.length} players`,
        onArm: () => socket.send(JSON.stringify({ type: 'arm', roundNumber: msg.roundNumber })),
        onStop: (attempt) => {
          socket.send(
            JSON.stringify({
              type: 'stop',
              roundNumber: msg.roundNumber,
              elapsedMs: attempt.elapsedMs,
              isFalseStart: attempt.isFalseStart,
            }),
          );
          if (destroyRound) destroyRound();
          destroyRound = undefined;
          renderWaitingForField();
        },
      });
      return;
    }
    if (msg.type === 'round-result') {
      if (destroyRound) destroyRound();
      destroyRound = undefined;
      if (msg.phase === 'final_duel') {
        uiState = 'between';
        renderFinalDuelRoundResult(msg);
        return;
      }
      remaining = msg.match.remaining;
      const amEliminated = myUserId !== null && !remaining.includes(myUserId);
      uiState = amEliminated ? 'eliminated-spectating' : 'between';
      renderEliminationAnnouncement(msg);
      return;
    }
    if (msg.type === 'match-complete') {
      remaining = msg.match.remaining;
      uiState = 'complete';
      if (destroyRound) destroyRound();
      destroyRound = undefined;
      renderComplete(msg.match, msg.ratingDeltas, msg.finalDuel);
      checkForNewAchievements();
      return;
    }
    if (msg.type === 'error') {
      uiState = 'error';
      renderError(msg.message);
    }
  }

  renderLobby();

  socket.onmessage = (event) => {
    try {
      handleServerMessage(JSON.parse(event.data as string) as ServerMessage);
    } catch {
      // ignore malformed frames
    }
  };
  socket.onerror = () => renderError('Could not reach the knockout server. Is it running?');

  return function destroy() {
    destroyed = true;
    if (destroyRound) destroyRound();
    socket.onmessage = null;
    socket.onerror = null;
  };
}
